import {FC, useCallback, useEffect, useMemo, useState} from "react";
import {Alert, App, Button, Card, ConfigProvider, Empty, InputNumber, Popconfirm, Select, Space, Table, Tag, Tooltip, Typography, theme} from "antd";
import {DeleteOutlined, LockOutlined, PlusOutlined, TeamOutlined} from "@ant-design/icons";
import AxiosServices from "../../Services/axiosServices";
import {IMatch} from "../../Interfaces/IMatch";
import {IGrpCmpEdt} from "../../Interfaces/IGrpCmpEdt";
import {matchAsUtcDate} from "../../Services/datetime";
import {useAuth} from "../../config/AuthContext";
import {useGlobal} from "../../config/GlobalContext";

const ACCENT = '#203923';
const {Paragraph} = Typography;

// How many "best thirds" the FIFA-style bracket repechs. Driven by
// the data CSV in practice — 8 for the World Cup competition (cmpEdt 9).
// Used to slice v_meilleurs_troisiemes when building the qualifiers
// string for bracket3rds/resolve.
const BEST_THIRDS_COUNT = 8;

type SlotType = 'G' | 'M' | '3';

type Slot = {
    slot_idt: number;
    dom_ext: 'D' | 'E';
    slot_type: SlotType;
    valeur: string;
    numero: number;
};

type MatchAttRow = {
    matchatt_idt: number;
    fifa_label: string | null;
    slots: Slot[];
};

type GroupStanding = {
    rang: number;
    team_idt: number;
    team: string;
};

type BestThird = {
    rang: number;
    grp_cod: string;
    team: string;
    tous_joues: number;
};

type SlotResolution =
    | {kind: 'team'; label: string; sub?: string}
    | {kind: 'unresolved'; label: string};

// Human-readable label for a slot — adapts to its Type:
//   "3e de A"  for groupe
//   "Vainqueur match #78" for match
//   "3e FIFA" for the dynamic FIFA-third slot
function slotLabel(slot: Slot): string {
    if (slot.slot_type === 'G') {
        const suffix = slot.numero === 1 ? 'er' : 'e';
        return `${slot.numero}${suffix} de ${slot.valeur}`;
    }
    if (slot.slot_type === 'M') {
        return `Vainqueur match n°${slot.numero}`;
    }
    return `3e FIFA`;
}

const AdminMatchesAtt: FC = () => {
    const {isAdmin, isLoggedIn} = useAuth();
    const {currentCmpEdt, groups} = useGlobal();
    const {message} = App.useApp();

    const [matches, setMatches] = useState<IMatch[]>([]);
    const [matchAttByMatchIdt, setMatchAttByMatchIdt] = useState<Map<number, MatchAttRow>>(new Map());
    const [standingsByGroup, setStandingsByGroup] = useState<Map<string, GroupStanding[]>>(new Map());
    const [bestThirds, setBestThirds] = useState<BestThird[]>([]);
    const [bracketAssignments, setBracketAssignments] = useState<Map<string, string>>(new Map());
    const [loading, setLoading] = useState(false);
    const [busyMatchIdt, setBusyMatchIdt] = useState<number | null>(null);

    const refresh = useCallback(async () => {
        if (!currentCmpEdt) return;
        setLoading(true);
        try {
            const list: IMatch[] = await new Promise((resolve, reject) =>
                AxiosServices.getMatches(resolve, currentCmpEdt).catch(reject),
            );
            setMatches(list ?? []);
            const rows = await AxiosServices.matchAttListByCmpEdt(currentCmpEdt);
            const map = new Map<number, MatchAttRow>();
            for (const r of rows) {
                const idt = Number(r.match_idt);
                if (!map.has(idt)) map.set(idt, {matchatt_idt: Number(r.matchatt_idt), fifa_label: r.fifa_label, slots: []});
                else {
                    // Keep the label seen on the first row of this MatchAtt
                    const existing = map.get(idt)!;
                    if (existing.fifa_label == null) existing.fifa_label = r.fifa_label;
                }
                if (r.slot_idt != null && r.dom_ext && r.slot_type && r.valeur != null && r.numero != null) {
                    map.get(idt)!.slots.push({
                        slot_idt: Number(r.slot_idt),
                        dom_ext: r.dom_ext,
                        slot_type: r.slot_type,
                        valeur: r.valeur,
                        numero: Number(r.numero),
                    });
                }
            }
            setMatchAttByMatchIdt(map);

            // Group standings — for 'G' slots
            const grpResults = await Promise.all(
                groups
                    .filter(g => !!g.Grp_Cod)
                    .map(g =>
                        AxiosServices.groupStandings(currentCmpEdt, g.Grp_Cod as string)
                            .then(rows => [g.Grp_Cod as string, rows] as const)
                            .catch(() => [g.Grp_Cod as string, [] as GroupStanding[]] as const),
                    ),
            );
            const grpMap = new Map<string, GroupStanding[]>();
            for (const [code, rows] of grpResults) grpMap.set(code, rows as GroupStanding[]);
            setStandingsByGroup(grpMap);

            // Best thirds + bracket assignments — for '3' slots
            const thirds: BestThird[] = await AxiosServices.getBestThirds(currentCmpEdt).catch(() => []);
            setBestThirds(thirds);
            const qualifiers = thirds.slice(0, BEST_THIRDS_COUNT).map(t => t.grp_cod).sort().join('');
            if (qualifiers.length > 0) {
                try {
                    const resp = await AxiosServices.bracket3rdsResolve(currentCmpEdt, qualifiers);
                    const am = new Map<string, string>();
                    for (const a of resp.assignments) am.set(a.match_label.toUpperCase(), a.third_grp.toUpperCase());
                    setBracketAssignments(am);
                } catch {
                    setBracketAssignments(new Map());
                }
            } else {
                setBracketAssignments(new Map());
            }
        } catch (err: any) {
            const msg = err?.response?.data?.error ?? err?.message ?? 'Erreur inconnue';
            message.error(`Impossible de charger : ${msg}`);
        } finally {
            setLoading(false);
        }
    }, [currentCmpEdt, message, groups]);

    useEffect(() => {
        if (!isAdmin || !currentCmpEdt) return;
        refresh();
    }, [isAdmin, currentCmpEdt, refresh]);

    const matchByNum = useMemo(() => {
        const m = new Map<number, IMatch>();
        for (const match of matches) m.set(Number(match.Num), match);
        return m;
    }, [matches]);

    // Group is "finished" when every group-phase match attached to
    // it carries match.finished = 1 (admin-controlled flag from
    // /admin/scores). Drives the confidence indicator on the "Match
    // résolu" tag. Knockout-stage matches are skipped: an A1 vs A2
    // bracket fixture also shares Grp_Cod = 'A' but its progress is
    // independent of the group phase's certainty.
    const groupAllFinished = useMemo(() => {
        const counts = new Map<string, {total: number; finished: number}>();
        for (const m of matches) {
            const code = m.Grp_Cod;
            if (!code) continue;
            // 'G' = phase de groupe; KO phases use other codes.
            if ((m.Phs_Cod ?? 'G').trim().toUpperCase() !== 'G') continue;
            const c = counts.get(code) ?? {total: 0, finished: 0};
            c.total += 1;
            if (Number(m.finished ?? 0) === 1) c.finished += 1;
            counts.set(code, c);
        }
        const result = new Map<string, boolean>();
        counts.forEach((c, code) => result.set(code, c.total > 0 && c.finished === c.total));
        return result;
    }, [matches]);

    const allBestThirdsSettled = useMemo(() => {
        return bestThirds.length > 0 && bestThirds.every(t => Number(t.tous_joues) === 1);
    }, [bestThirds]);

    const bestThirdsByGrp = useMemo(() => {
        const m = new Map<string, BestThird>();
        for (const t of bestThirds) m.set(t.grp_cod, t);
        return m;
    }, [bestThirds]);

    // Returns the implicit FIFA label of a MatchAtt, derived from
    // the row's first-rank group slot (e.g. (G, A, 1) ⇒ "1A"). Falls
    // back to the explicit fifa_label if no such slot is present.
    const inferFifaLabel = useCallback((row: MatchAttRow): string | null => {
        const firstRankGrp = row.slots.find(s => s.slot_type === 'G' && Number(s.numero) === 1);
        if (firstRankGrp) return `1${firstRankGrp.valeur.toUpperCase()}`;
        return row.fifa_label ? row.fifa_label.toUpperCase() : null;
    }, []);

    // A slot is "certain" when its resolution can't change anymore:
    //   - 'G': the source group is finished.
    //   - 'M': the source match is finished.
    //   - '3': the FIFA bracket has settled (every candidate third's
    //     group is finished, so the best-thirds ranking is stable)
    //     AND the resolved third's own group is finished.
    const isSlotCertain = useCallback((slot: Slot, row: MatchAttRow): boolean => {
        if (slot.slot_type === 'G') {
            return groupAllFinished.get(slot.valeur) === true;
        }
        if (slot.slot_type === 'M') {
            const m = matchByNum.get(Number(slot.numero));
            return !!m && Number(m.finished ?? 0) === 1;
        }
        // Type '3'
        if (!allBestThirdsSettled) return false;
        const fifaLabel = inferFifaLabel(row);
        if (!fifaLabel) return false;
        const thirdGrp = bracketAssignments.get(fifaLabel);
        if (!thirdGrp) return false;
        return groupAllFinished.get(thirdGrp) === true;
    }, [groupAllFinished, matchByNum, allBestThirdsSettled, inferFifaLabel, bracketAssignments]);

    const resolveSlot = useCallback((slot: Slot, row: MatchAttRow): SlotResolution => {
        if (slot.slot_type === 'G') {
            const standings = standingsByGroup.get(slot.valeur);
            const standingRow = standings?.find(r => Number(r.rang) === Number(slot.numero));
            if (!standingRow) return {kind: 'unresolved', label: '? (groupe non joué)'};
            return {kind: 'team', label: standingRow.team};
        }
        if (slot.slot_type === 'M') {
            const m = matchByNum.get(Number(slot.numero));
            if (!m) return {kind: 'unresolved', label: `? (match n°${slot.numero} introuvable)`};
            const sd = Number(m.Score_Dom ?? -1);
            const se = Number(m.Score_Ext ?? -1);
            if (sd < 0 || se < 0) return {kind: 'unresolved', label: '? (match non joué)'};
            if (sd === se) return {kind: 'unresolved', label: '? (match nul)'};
            const winnerName = sd > se ? m.Nom_Dom : m.Nom_Ext;
            return {kind: 'team', label: winnerName ?? '?', sub: `${m.Nom_Dom} ${sd}-${se} ${m.Nom_Ext}`};
        }
        // Type '3' — FIFA-designated third for the MatchAtt's label
        const fifaLabel = inferFifaLabel(row);
        if (!fifaLabel) return {kind: 'unresolved', label: '? (pas de slot G rang 1 pour déduire le label)'};
        const thirdGrp = bracketAssignments.get(fifaLabel);
        if (!thirdGrp) return {kind: 'unresolved', label: `? (label ${fifaLabel} absent du tableau FIFA)`};
        const third = bestThirdsByGrp.get(thirdGrp);
        if (!third) return {kind: 'unresolved', label: `? (3e de ${thirdGrp} introuvable)`};
        const sub = `3e du groupe ${thirdGrp}${Number(third.tous_joues) ? '' : ' — groupe non terminé'}`;
        return {kind: 'team', label: third.team, sub};
    }, [standingsByGroup, matchByNum, bracketAssignments, bestThirdsByGrp, inferFifaLabel]);

    const upcoming = useMemo(() => {
        return matches
            .filter(m => !m.team_idt_Dom || !m.team_idt_Ext)
            .sort((a, b) => {
                const ta = matchAsUtcDate(a.Dte, a.Hre)?.getTime() ?? Number.POSITIVE_INFINITY;
                const tb = matchAsUtcDate(b.Dte, b.Hre)?.getTime() ?? Number.POSITIVE_INFINITY;
                return ta - tb || (a.Num ?? 0) - (b.Num ?? 0);
            });
    }, [matches]);

    const ensureMatchAtt = async (match: IMatch, fifa_label?: string): Promise<number> => {
        const existing = matchAttByMatchIdt.get(match.Idt);
        if (existing) return existing.matchatt_idt;
        const {matchatt_idt} = await AxiosServices.matchAttCreate(match.Idt, [], fifa_label ?? null);
        setMatchAttByMatchIdt(prev => {
            const next = new Map(prev);
            next.set(match.Idt, {matchatt_idt, fifa_label: fifa_label ?? null, slots: []});
            return next;
        });
        return matchatt_idt;
    };

    const addSlot = async (match: IMatch, slot: {DomExt: 'D' | 'E'; Type: SlotType; Valeur: string; Numero: number}) => {
        setBusyMatchIdt(match.Idt);
        try {
            const matchatt_idt = await ensureMatchAtt(match);
            await AxiosServices.matchAttAddSlot(matchatt_idt, slot);
            await refresh();
            message.success(`Slot ajouté.`);
        } catch (err: any) {
            const status = err?.response?.status;
            const msg = err?.response?.data?.error ?? err?.response?.data?.message ?? err?.message;
            message.error(`Échec ${status ?? ''} : ${msg ?? 'erreur inconnue'}`);
        } finally {
            setBusyMatchIdt(null);
        }
    };

    const removeSlot = async (match: IMatch, slot: Slot) => {
        setBusyMatchIdt(match.Idt);
        try {
            await AxiosServices.matchAttRemoveSlot(slot.slot_idt);
            await refresh();
            message.success(`Slot supprimé.`);
        } catch (err: any) {
            const status = err?.response?.status;
            const msg = err?.response?.data?.error ?? err?.response?.data?.message ?? err?.message;
            message.error(`Échec ${status ?? ''} : ${msg ?? 'erreur inconnue'}`);
        } finally {
            setBusyMatchIdt(null);
        }
    };

    if (!isLoggedIn || !isAdmin) {
        return (
            <div style={{padding: 24}}>
                <Alert type="error" showIcon icon={<LockOutlined/>} message="Accès refusé"
                       description="Cette page est réservée aux administrateurs."/>
            </div>
        );
    }

    const groupOptions = groups
        .filter(g => !!g.Grp_Cod)
        .map((g: IGrpCmpEdt) => ({value: g.Grp_Cod as string, label: `${g.Grp_Cod} — ${g.lib}`}));

    const renderSideSlots = (match: IMatch, side: 'D' | 'E') => {
        const row = matchAttByMatchIdt.get(match.Idt);
        const slots = (row?.slots ?? []).filter(s => s.dom_ext === side);
        return (
            <SlotEditor
                slots={slots}
                row={row ?? null}
                groupOptions={groupOptions}
                standingsByGroup={standingsByGroup}
                disabled={busyMatchIdt === match.Idt}
                onAdd={(input) => addSlot(match, {DomExt: side, ...input})}
                onRemove={(slot) => removeSlot(match, slot)}
                resolveSlot={resolveSlot}
            />
        );
    };

    const columns = [
        {
            title: 'N°',
            dataIndex: 'Num',
            key: 'num',
            width: 60,
        },
        {
            title: 'Date',
            key: 'dte',
            render: (_: any, m: IMatch) => {
                const d = matchAsUtcDate(m.Dte, m.Hre);
                return d ? d.toLocaleString() : '—';
            },
        },
        {
            title: 'Domicile',
            key: 'dom',
            render: (_: any, m: IMatch) => renderSideSlots(m, 'D'),
        },
        {
            title: 'Extérieur',
            key: 'ext',
            render: (_: any, m: IMatch) => renderSideSlots(m, 'E'),
        },
        {
            title: 'Match résolu',
            key: 'resolved',
            render: (_: any, m: IMatch) => {
                const row = matchAttByMatchIdt.get(m.Idt);
                if (!row) return <Tag>—</Tag>;
                type Resolved = {label: string; certain: boolean};
                const resolvedSide = (side: 'D' | 'E'): Resolved | null => {
                    const slots = row.slots.filter(s => s.dom_ext === side);
                    if (slots.length === 0) return null;
                    const teamEntries = slots
                        .map(s => ({slot: s, res: resolveSlot(s, row)}))
                        .filter(e => e.res.kind === 'team') as Array<{slot: Slot; res: {kind: 'team'; label: string}}>;
                    if (teamEntries.length === 0) return null;
                    // Certain only when exactly one candidate AND its
                    // own slot is settled (group / match finished, or
                    // FIFA bracket fully resolved for Type='3').
                    const certain = teamEntries.length === 1 && isSlotCertain(teamEntries[0].slot, row);
                    if (teamEntries.length === 1) return {label: teamEntries[0].res.label, certain};
                    return {label: `(${teamEntries.map(e => e.res.label).join(' / ')})`, certain};
                };
                const dom = resolvedSide('D');
                const ext = resolvedSide('E');
                if (!dom && !ext) return <Tag style={{opacity: 0.55}}>en attente</Tag>;
                const certain = (dom?.certain ?? true) && (ext?.certain ?? true) && dom !== null && ext !== null;
                return (
                    <Tag color={certain ? 'green' : 'warning'} style={{whiteSpace: 'nowrap'}}>
                        {dom?.label ?? '?'} vs {ext?.label ?? '?'}
                    </Tag>
                );
            },
        },
        {
            title: 'Actions',
            key: 'actions',
            align: 'center' as const,
            width: 90,
            render: (_: any, m: IMatch) => {
                const row = matchAttByMatchIdt.get(m.Idt);
                if (!row) return <Tag>Pas de prévisionnel</Tag>;
                return (
                    <Popconfirm
                        title="Supprimer le prévisionnel"
                        description={`Effacer tous les slots du match n°${m.Num} ?`}
                        okText="Supprimer"
                        okButtonProps={{danger: true}}
                        cancelText="Annuler"
                        onConfirm={async () => {
                            setBusyMatchIdt(m.Idt);
                            try {
                                await AxiosServices.matchAttDelete(row.matchatt_idt);
                                await refresh();
                                message.success(`Prévisionnel n°${m.Num} supprimé.`);
                            } catch (err: any) {
                                message.error(err?.response?.data?.error ?? err?.message ?? 'Échec');
                            } finally {
                                setBusyMatchIdt(null);
                            }
                        }}
                    >
                        <Button danger size="small" icon={<DeleteOutlined/>} loading={busyMatchIdt === m.Idt}/>
                    </Popconfirm>
                );
            },
        },
    ];

    return (
        <div style={{padding: 16, height: '100%', boxSizing: 'border-box', background: ACCENT, overflow: 'auto'}}>
            <Card
                styles={{header: {background: ACCENT, color: '#fff'}, body: {background: ACCENT, color: '#fff'}}}
                style={{background: ACCENT, borderColor: ACCENT}}
                title={<Space><TeamOutlined/><span>Matchs prévisionnels</span></Space>}
            >
                <Paragraph style={{color: '#cfd8d3'}}>
                    Trois types de slots :
                    {' '}<Tag color="geekblue">Groupe</Tag> (Xe de A),
                    {' '}<Tag color="purple">Vainqueur match</Tag> (W du match n°78),
                    {' '}<Tag color="volcano">3e FIFA</Tag> (le 3e que le tableau FIFA assigne au match).
                    Le label FIFA est déduit automatiquement du slot Groupe de rang 1 du même MatchAtt
                    (ex : un côté Dom = 1<sup>er</sup> de A ⇒ label 1A pour le slot 3e FIFA de l'Ext).
                    Plusieurs slots côté = OU logique.
                </Paragraph>
                <ConfigProvider theme={{algorithm: theme.darkAlgorithm}}>
                    {upcoming.length === 0 ? (
                        <Empty description={<span style={{color: '#cfd8d3'}}>Aucun match prévisionnel à éditer.</span>}/>
                    ) : (
                        <Table
                            dataSource={upcoming}
                            columns={columns}
                            rowKey={(m) => m.Idt.toString()}
                            loading={loading}
                            pagination={false}
                            size="small"
                            sticky
                            scroll={{y: 'calc(100vh - 280px)'}}
                        />
                    )}
                </ConfigProvider>
            </Card>
        </div>
    );
};

type SlotEditorProps = {
    slots: Slot[];
    row: MatchAttRow | null;
    groupOptions: Array<{value: string; label: string}>;
    standingsByGroup: Map<string, GroupStanding[]>;
    disabled: boolean;
    onAdd: (input: {Type: SlotType; Valeur: string; Numero: number}) => void;
    onRemove: (slot: Slot) => void;
    resolveSlot: (slot: Slot, row: MatchAttRow) => SlotResolution;
};

const SlotEditor: FC<SlotEditorProps> = ({slots, row, groupOptions, standingsByGroup, disabled, onAdd, onRemove, resolveSlot}) => {
    const [slotType, setSlotType] = useState<SlotType>('G');
    const [grp, setGrp] = useState<string | undefined>(undefined);
    const [rang, setRang] = useState<number | null>(1);
    const [matchNum, setMatchNum] = useState<number | null>(null);

    const canAddGroupSlot = slotType === 'G' && !!grp && !!rang && rang >= 1;
    const canAddMatchSlot = slotType === 'M' && !!matchNum && matchNum >= 1;
    // Type='3' is always addable; resolution will report a clear
    // unresolved status if the parent MatchAtt has no slot G rang 1
    // (and no explicit fifa_label) to derive the label from.
    const canAddFifaThird = slotType === '3';
    const canAdd = canAddGroupSlot || canAddMatchSlot || canAddFifaThird;

    const handleAdd = () => {
        if (slotType === 'G' && grp && rang) {
            onAdd({Type: 'G', Valeur: grp, Numero: rang});
            setGrp(undefined);
            setRang(1);
        } else if (slotType === 'M' && matchNum) {
            onAdd({Type: 'M', Valeur: 'W', Numero: matchNum});
            setMatchNum(null);
        } else if (slotType === '3') {
            onAdd({Type: '3', Valeur: '3', Numero: 1});
        }
    };

    const chipColor = (t: SlotType): string =>
        t === 'G' ? 'geekblue' : t === 'M' ? 'purple' : 'volcano';

    return (
        <Space direction="vertical" size={6} style={{width: '100%'}}>
            <Space direction="vertical" size={2} style={{width: '100%'}}>
                {slots.length === 0 ? (
                    <Tag style={{opacity: 0.55, fontStyle: 'italic'}}>Aucun slot</Tag>
                ) : (
                    slots.map(s => {
                        // resolveSlot needs the row to derive the FIFA
                        // label from the row's slot G rang 1. When the
                        // row hasn't been persisted yet (e.g. before
                        // the first slot is saved) there's nothing to
                        // resolve.
                        if (!row) return null;
                        const r = resolveSlot(s, row);
                        return (
                            <div key={s.slot_idt} style={{display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap'}}>
                                <Tag
                                    color={chipColor(s.slot_type)}
                                    closable
                                    onClose={(e) => { e.preventDefault(); onRemove(s); }}
                                    style={{margin: 0}}
                                >
                                    {slotLabel(s)}
                                </Tag>
                                <span style={{fontSize: 11, opacity: 0.8}}>→ {r.label}</span>
                                {r.kind === 'team' && r.sub && (
                                    <span style={{fontSize: 10, opacity: 0.55}}>({r.sub})</span>
                                )}
                            </div>
                        );
                    })
                )}
            </Space>
            <Space size={4} wrap>
                <Select
                    value={slotType}
                    onChange={(v) => setSlotType(v)}
                    options={[
                        {value: 'G', label: 'Groupe'},
                        {value: 'M', label: 'Vainqueur match'},
                        {value: '3', label: '3e FIFA'},
                    ]}
                    style={{width: 140}}
                    size="small"
                />
                {slotType === 'G' && (
                    <>
                        <Select
                            placeholder="Groupe"
                            value={grp}
                            onChange={(v) => { setGrp(v); setRang(1); }}
                            options={groupOptions}
                            style={{minWidth: 110}}
                            size="small"
                            showSearch
                        />
                        <RankSelect
                            grp={grp}
                            standings={grp ? standingsByGroup.get(grp) ?? [] : []}
                            value={rang}
                            onChange={(v) => setRang(v)}
                        />
                    </>
                )}
                {slotType === 'M' && (
                    <InputNumber
                        placeholder="N° match"
                        min={1}
                        max={999}
                        value={matchNum}
                        onChange={(v) => setMatchNum(v ?? null)}
                        size="small"
                        style={{width: 90}}
                    />
                )}
                <Tooltip title={!canAdd ? 'Compléter les champs' : 'Ajouter ce slot'}>
                    <Button
                        size="small"
                        type="primary"
                        icon={<PlusOutlined/>}
                        disabled={disabled || !canAdd}
                        onClick={handleAdd}
                    />
                </Tooltip>
            </Space>
        </Space>
    );
};

type RankSelectProps = {
    grp: string | undefined;
    standings: GroupStanding[];
    value: number | null;
    onChange: (rang: number | null) => void;
};

// Drop-in replacement for the rank InputNumber: shows '1 - Team A',
// '2 - Team B', etc. for the selected group. Falls back to a plain
// '1 / 2 / 3 / 4' list when the group has no standings yet (=group
// not played), so the admin can still record the slot ahead of the
// games. Stays disabled while no group is selected.
const RankSelect: FC<RankSelectProps> = ({grp, standings, value, onChange}) => {
    const options = standings.length > 0
        ? [...standings]
            .sort((a, b) => Number(a.rang) - Number(b.rang))
            .map(s => ({value: Number(s.rang), label: `${s.rang} — ${s.team}`}))
        : [1, 2, 3, 4].map(n => ({value: n, label: `${n}`}));
    return (
        <Select
            placeholder="Rang"
            value={value ?? undefined}
            onChange={(v) => onChange(typeof v === 'number' ? v : null)}
            options={options}
            disabled={!grp}
            size="small"
            style={{minWidth: 160}}
        />
    );
};

export default AdminMatchesAtt;
